import { useEffect, useMemo, useState } from 'react'
import { Pill, Printer } from 'lucide-react'
import { bName, bUnit, bTypeTag } from '../../lib/billing.js'
import { apiGet } from '../../lib/api.js'
import { Modal, Button, Checkbox, InlineError, fmtDateText } from '../../kit'
import { buildMedLabelHtml, openMedLabelPrint } from '../../lib/medLabel.js'
import { MED_CLINIC, medDefaults } from '../../data/medicationLabels.js'

/**
 * พิมพ์ฉลากยา — one 80 × 60 mm thermal label per medication billing line.
 *
 * READ-ONLY BY DESIGN (owner's call, 2026-10-16): every field on the label is
 * pulled from the system — patient/HN from the patient, drug + จำนวน from the
 * billing line, doctor + date from the treatment, วิธีใช้/ข้อบ่งชี้/คำเตือน from
 * data/medicationLabels.js. Staff only choose WHICH labels to print. A label
 * that needs different wording is a data fix (the billing line or the defaults
 * file), not a per-print edit — so what prints is always what the system holds.
 *
 * A medication with no วิธีใช้ in the defaults file cannot be printed: its
 * checkbox is disabled and says why, rather than printing a label with no dose.
 *
 * Read-only on the visit too: no billing write, so the day lock does not apply.
 */

export const MEDICATION_TYPE = 'medication'

// A line is medication when its billing_type says so. The nested billing_type
// is usually on the line; `typeById` (the billing-type catalogue) covers a line
// whose nested object was not expanded.
export function isMedicationLine(b, typeById) {
  if (bTypeTag(b) === MEDICATION_TYPE) return true
  const bt = typeById?.get?.(Number(b?.billing_type_id))
  return bt?.type === MEDICATION_TYPE
}

function patientFullName(p) {
  if (!p) return ''
  return [p.prefix, p.name, p.surname].filter(Boolean).join(' ').trim() || String(p.fullname || '').trim()
}

// "ทพ.ธนิตย์ วนิชนพรัตน์ (ท.9514)" — from the FULL doctor record (/api/doctors/:id).
// The doctor embedded on a treatment is slim: its name is the short `code`
// ("เอก ธนิตย์"), which is not who a patient should see prescribed their drug.
// `prefix` is a title ("ทพ.", "อ.ทพญ.") except for junk rows ("-", a number);
// `license` holds placeholders on some rows ("0000", "9999") — a repeated digit
// is not a licence, so it is left off rather than printed.
function doctorLine(full, t) {
  const d = full || {}
  const prefix = /[ก-๙]/.test(String(d.prefix || '')) ? String(d.prefix).trim() : ''
  const name = [d.name, d.surname].filter(Boolean).join(' ').trim()
  if (!name) {
    const td = t?.doctor || {}
    return String(td.fullname || td.full_name || td.name || td.code || '').trim()
  }
  const lic = String(d.license ?? '').trim()
  const realLic = /^\d{3,}$/.test(lic) && !/^(\d)\1+$/.test(lic)
  return `${prefix}${name}${realLic ? ` (ท.${lic})` : ''}`
}

// '10 ส.ค. 2026' — ค.ศ. with Thai month names, exactly what the label printed
// before (owner-approved label layout; not re-dated to พ.ศ. here). No visit
// date → today, as before.
function thaiDate(v) {
  const s = fmtDateText(v)
  return s === '—' ? fmtDateText(Date.now()) : s
}

function buildLabel(b, header) {
  const def = medDefaults(b.billing_type_id)
  return {
    ...header,
    key: String(b.id ?? b.billing_type_id),
    drug: bName(b),
    drugTh: def.th,
    qty: String(bUnit(b) || 1),
    unit: def.unit,
    sig: def.sig,
    indication: def.indication,
    warning: def.warning,
  }
}

// 80 × 60 mm at CSS 96dpi, shown at PREVIEW_SCALE. The iframe is EXACTLY one
// label in size and the preview document has overflow hidden, so there is
// never a scrollbar in either direction.
const LABEL_W = 302, LABEL_H = 227, PREVIEW_SCALE = 1.25

export default function MedLabelDialog({ lines, treatment, patient, branch, onClose }) {
  // Full doctor record — printing waits for it (see `doctorReady`) so a label
  // never goes out with the short code because someone clicked fast.
  const did = treatment?.doctor_id ?? treatment?.doctor?.id
  const [doctorRec, setDoctorRec] = useState(null)
  const [doctorReady, setDoctorReady] = useState(!did)
  useEffect(() => {
    if (!did) return
    let alive = true
    apiGet(`/api/doctors/${did}`)
      .then((r) => { if (alive) setDoctorRec(r) })
      .catch(() => { /* falls back to the treatment's doctor */ })
      .finally(() => { if (alive) setDoctorReady(true) })
    return () => { alive = false }
  }, [did])

  const labels = useMemo(() => {
    const header = {
      clinic: [MED_CLINIC.name, branch ? `สาขา ${branch}` : ''].filter(Boolean).join(' '),
      patient: patientFullName(patient),
      hn: patient?.hn || patient?.HN || '',
      doctor: doctorLine(doctorRec, treatment),
      // The visit date = the day the drug was dispensed (billed on that visit).
      date: thaiDate(treatment?.date),
    }
    return lines.map((b) => buildLabel(b, header))
  }, [lines, treatment, patient, branch, doctorRec])

  const printable = (l) => !!l.sig.trim()
  const [picked, setPicked] = useState(() => new Set(labels.filter(printable).map((l) => l.key)))
  const [focus, setFocus] = useState(() => labels[0]?.key)
  const [err, setErr] = useState('')

  const toggle = (key) => setPicked((s) => {
    const n = new Set(s)
    n.has(key) ? n.delete(key) : n.add(key)
    return n
  })
  const chosen = labels.filter((l) => picked.has(l.key) && printable(l))
  const previewLabel = labels.find((l) => l.key === focus) || labels[0]
  const previewHtml = useMemo(
    () => (previewLabel ? buildMedLabelHtml([previewLabel], { preview: true }) : ''),
    [previewLabel],
  )

  function print() {
    setErr('')
    if (!chosen.length) { setErr('เลือกยาอย่างน้อย 1 รายการ'); return }
    if (!openMedLabelPrint(chosen)) setErr('เปิดหน้าต่างพิมพ์ไม่ได้ — อนุญาต pop-up ของเว็บนี้ก่อน')
  }

  return (
    <Modal
      onClose={onClose}
      width="lg"
      title="พิมพ์ฉลากยา"
      icon={Pill}
      subtitle="สติกเกอร์ความร้อน 80 × 60 มม. · 1 ฉลาก/ยา"
      footer={(
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0 break-words"><InlineError>{err}</InlineError></div>
          <Button variant="secondary" onClick={onClose}>ยกเลิก</Button>
          <Button icon={Printer} onClick={print} disabled={!chosen.length || !doctorReady}>
            พิมพ์ {chosen.length} ฉลาก
          </Button>
        </div>
      )}
    >
      <div className="flex flex-col md:flex-row gap-5">
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="text-[11.5px] uppercase tracking-wide text-slate-400 mb-1">เลือกฉลากที่จะพิมพ์</div>
          {labels.map((l) => {
            const ok = printable(l)
            const active = previewLabel?.key === l.key
            return (
              <div key={l.key} onClick={() => setFocus(l.key)}
                className={`flex items-start gap-2.5 rounded-xl border px-3 py-2 cursor-pointer transition-colors ${
                  active ? 'border-brand-300 bg-brand-50/40' : 'border-slate-200 hover:bg-slate-50'
                }`}>
                {/* The wrapper stops the click reaching the row, so ticking a box does
                    not also move the preview (the row's own click does that). */}
                <span className="flex-none" onClick={(e) => e.stopPropagation()}>
                  <Checkbox disabled={!ok} checked={ok && picked.has(l.key)} onChange={() => toggle(l.key)} aria-label={`พิมพ์ฉลาก ${l.drug}`} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="font-semibold text-[14px] text-slate-800 truncate">{l.drug}</span>
                    <span className="text-[12px] text-slate-500 tabular-nums whitespace-nowrap">× {l.qty}{l.unit ? ` ${l.unit}` : ''}</span>
                  </div>
                  {ok
                    ? <div className="text-[12px] text-slate-500 line-clamp-1">{l.sig}</div>
                    : <div className="text-[12px] text-amber-600">ยังไม่มีวิธีใช้ของยานี้ในระบบ — พิมพ์ไม่ได้</div>}
                </div>
              </div>
            )
          })}
        </div>

        <div className="flex-none">
          <div className="text-[11.5px] uppercase tracking-wide text-slate-400 mb-1.5 truncate" style={{ maxWidth: LABEL_W * PREVIEW_SCALE }}>
            ตัวอย่างฉลาก · {previewLabel?.drug}
          </div>
          {/* Same document the print window gets (preview mode = one label,
              no backdrop, overflow hidden), scaled up for reading. The outer
              box reserves the SCALED size so the dialog lays out around it. */}
          <div className="rounded-lg ring-1 ring-slate-300 shadow-sm overflow-hidden bg-white"
            style={{ width: LABEL_W * PREVIEW_SCALE, height: LABEL_H * PREVIEW_SCALE }}>
            <iframe title="ตัวอย่างฉลากยา" srcDoc={previewHtml} sandbox="allow-scripts" scrolling="no"
              style={{ width: LABEL_W, height: LABEL_H, border: 0, transform: `scale(${PREVIEW_SCALE})`, transformOrigin: '0 0', display: 'block' }} />
          </div>
        </div>
      </div>
    </Modal>
  )
}
